import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaArrowRight, FaCheckCircle, FaDesktop, FaPaperPlane, FaStar } from 'react-icons/fa';

import dashboardShowcase from '../../assets/showcase/admin-dashboard-showcase.svg';
import broadcastShowcase from '../../assets/showcase/broadcast-message-showcase.svg';

const Section = styled.section`
  position: relative;
  overflow: hidden;
  padding: 6rem 1rem;
  background:
    radial-gradient(circle at 12% 18%, rgba(79, 70, 229, 0.14), transparent 28%),
    radial-gradient(circle at 85% 16%, rgba(14, 165, 233, 0.16), transparent 28%),
    linear-gradient(180deg, #f4f7ff 0%, #eef2ff 48%, #ffffff 100%);
  color: #0f172a;
`;

const Glow = styled.div`
  position: absolute;
  inset: auto;
  width: 420px;
  height: 420px;
  border-radius: 999px;
  filter: blur(70px);
  opacity: 0.45;
  pointer-events: none;

  &:first-child {
    top: -120px;
    right: -80px;
    background: rgba(99, 102, 241, 0.22);
  }

  &:last-child {
    bottom: -120px;
    left: -80px;
    background: rgba(56, 189, 248, 0.2);
  }
`;

const Container = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1260px;
  margin: 0 auto;
  display: grid;
  gap: 2.4rem;

  @media (min-width: 1100px) {
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
    align-items: start;
  }
`;

const CopyColumn = styled(motion.div)`
  max-width: 540px;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.5rem 0.9rem;
  border-radius: 999px;
  background: rgba(79, 70, 229, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.12);
  color: #4338ca;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Title = styled.h2`
  margin: 1.15rem 0 0;
  font-size: clamp(2rem, 4vw, 3.45rem);
  line-height: 1.02;
  font-weight: 800;
  letter-spacing: -0.04em;
  color: #0f172a;
`;

const GradientText = styled.span`
  background: linear-gradient(90deg, #4f46e5 0%, #0ea5e9 55%, #14b8a6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  margin: 1.2rem 0 0;
  color: #475569;
  font-size: 1.02rem;
  line-height: 1.85;
`;

const FeatureList = styled.div`
  display: grid;
  gap: 0.9rem;
  margin-top: 1.7rem;
`;

const FeatureRow = styled.div`
  display: flex;
  gap: 0.8rem;
  align-items: flex-start;
  padding: 1rem 1.05rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.05);
`;

const FeatureIconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 13px;
  background: linear-gradient(145deg, rgba(79, 70, 229, 0.14), rgba(14, 165, 233, 0.14));
  display: grid;
  place-items: center;
  color: #4338ca;
  flex-shrink: 0;
`;

const FeatureTitle = styled.div`
  font-size: 0.98rem;
  font-weight: 800;
  color: #0f172a;
`;

const FeatureText = styled.div`
  margin-top: 0.18rem;
  font-size: 0.9rem;
  line-height: 1.65;
  color: #475569;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;
  margin-top: 1.8rem;
`;

const PrimaryAction = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.95rem 1.25rem;
  border-radius: 16px;
  background: linear-gradient(90deg, #4f46e5 0%, #0ea5e9 100%);
  color: #ffffff;
  text-decoration: none;
  font-weight: 800;
  box-shadow: 0 18px 38px rgba(79, 70, 229, 0.24);
  transition: transform 0.22s ease, box-shadow 0.22s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 22px 42px rgba(79, 70, 229, 0.3);
  }
`;

const SecondaryAction = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.95rem 1.2rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(148, 163, 184, 0.2);
  color: #0f172a;
  text-decoration: none;
  font-weight: 700;
  transition: transform 0.22s ease, background 0.22s ease;

  &:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.95);
  }
`;

const ShowcaseGrid = styled.div`
  display: grid;
  gap: 1.4rem;
`;

const ScreenshotCard = styled(motion.div)`
  border-radius: 28px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 28px 70px rgba(15, 23, 42, 0.12);
  backdrop-filter: blur(18px);
`;

const ScreenshotMeta = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.15rem 1.2rem 0.95rem;
`;

const ScreenshotTitle = styled.div`
  font-size: 1rem;
  font-weight: 800;
  color: #0f172a;
`;

const ScreenshotText = styled.div`
  margin-top: 0.25rem;
  font-size: 0.9rem;
  line-height: 1.6;
  color: #475569;
`;

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.48rem 0.75rem;
  border-radius: 999px;
  background: rgba(79, 70, 229, 0.08);
  color: #4338ca;
  font-size: 0.76rem;
  font-weight: 800;
  white-space: nowrap;
`;

const ScreenshotFrame = styled.div`
  position: relative;
  padding: 0 1rem 1rem;
`;

const ScreenshotImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border-radius: 22px;
  border: 1px solid rgba(203, 213, 225, 0.7);
  background: #e2e8f0;
`;

const CaptionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  padding: 0 1rem 1.1rem;
`;

const CaptionChip = styled.div`
  padding: 0.5rem 0.75rem;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 0.78rem;
  font-weight: 700;
`;

const AppScreenshotsSection = () => {
  const screenshotCards = [
    {
      title: 'Admin Dashboard Overview',
      text: 'A broad school operations view with finance, attendance, transport, exams, and reports visible in one place.',
      image: dashboardShowcase,
      pill: 'Dashboard',
      chips: ['Home workspace', 'Finance snapshot', 'Attendance analytics', 'Action shortcuts'],
    },
    {
      title: 'Broadcast Message Workflow',
      text: 'A communication-first screen for targeting audiences, choosing delivery channels, composing updates, and previewing the mobile result live.',
      image: broadcastShowcase,
      pill: 'Communication',
      chips: ['Audience targeting', 'SMS / WhatsApp / Push', 'Live preview', 'Media attachment'],
    },
  ];

  return (
    <Section id="app-screenshots">
      <Glow />
      <Glow />
      <Container>
        <CopyColumn
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.28 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <Eyebrow>
            <FaStar />
            Product Showcase
          </Eyebrow>
          <Title>
            Real product views that make the platform feel <GradientText>immediately usable</GradientText>
          </Title>
          <Description>
            These showcase screens highlight how Skhool brings school operations and communication into one polished
            flow. Instead of abstract feature claims, the landing page now shows the actual experience prospects care
            about when they evaluate the product.
          </Description>

          <FeatureList>
            <FeatureRow>
              <FeatureIconWrap><FaDesktop /></FeatureIconWrap>
              <div>
                <FeatureTitle>Command-center clarity</FeatureTitle>
                <FeatureText>
                  The admin dashboard groups core metrics, quick actions, and day-to-day monitoring into a layout that
                  feels executive without becoming overwhelming.
                </FeatureText>
              </div>
            </FeatureRow>

            <FeatureRow>
              <FeatureIconWrap><FaPaperPlane /></FeatureIconWrap>
              <div>
                <FeatureTitle>Communication with proof, not promises</FeatureTitle>
                <FeatureText>
                  The broadcast screen shows how messages move from audience selection to channel choice to a mobile
                  preview, which is exactly the kind of workflow buyers want to see.
                </FeatureText>
              </div>
            </FeatureRow>

            <FeatureRow>
              <FeatureIconWrap><FaCheckCircle /></FeatureIconWrap>
              <div>
                <FeatureTitle>Showcase built for demos</FeatureTitle>
                <FeatureText>
                  The section is responsive, image-led, and easy to extend later with more role-based screens like
                  transport, exams, fees, or mobile app views.
                </FeatureText>
              </div>
            </FeatureRow>
          </FeatureList>

          <Actions>
            <PrimaryAction href="https://app.skhool.in/register-school">
              Start Your School Setup
              <FaArrowRight />
            </PrimaryAction>
            <SecondaryAction href="https://webdemo.skhool.in/">
              View Website Demo
              <FaArrowRight />
            </SecondaryAction>
          </Actions>
        </CopyColumn>

        <ShowcaseGrid>
          {screenshotCards.map((card, index) => (
            <ScreenshotCard
              key={card.title}
              initial={{ opacity: 0, y: 32, scale: 0.98 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.16 }}
              transition={{ duration: 0.62, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
            >
              <ScreenshotMeta>
                <div>
                  <ScreenshotTitle>{card.title}</ScreenshotTitle>
                  <ScreenshotText>{card.text}</ScreenshotText>
                </div>
                <Pill>{card.pill}</Pill>
              </ScreenshotMeta>

              <ScreenshotFrame>
                <ScreenshotImage src={card.image} alt={card.title} />
              </ScreenshotFrame>

              <CaptionRow>
                {card.chips.map((chip) => (
                  <CaptionChip key={chip}>{chip}</CaptionChip>
                ))}
              </CaptionRow>
            </ScreenshotCard>
          ))}
        </ShowcaseGrid>
      </Container>
    </Section>
  );
};

export default AppScreenshotsSection;
